/**
 * Kyle Rising — Bandsintown shows feed.
 *
 * Replaces the section's manually entered rows with live dates. Progressive by
 * design: whatever is in the markup renders first, and is only swapped once the
 * feed returns. If Bandsintown is down, slow or misconfigured, the page keeps
 * whatever was already there rather than collapsing to an empty list.
 */
class KRShows extends HTMLElement {
  async connectedCallback() {
    const artist = this.dataset.artist?.trim();
    const appId = this.dataset.appId?.trim();
    if (!artist || !appId) return;

    const list = this.querySelector('[data-kr-shows-list]');
    const empty = this.querySelector('[data-kr-shows-empty]');
    if (!list) return;

    const limit = parseInt(this.dataset.limit || '0', 10);
    const when = this.dataset.when === 'past' ? 'past' : 'upcoming';
    this.isPast = when === 'past';

    const endpoint =
      `https://rest.bandsintown.com/artists/${encodeURIComponent(artist)}/events` +
      `?app_id=${encodeURIComponent(appId)}&date=${when}`;

    let events;
    try {
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Bandsintown responded ${response.status}`);
      events = await response.json();
    } catch (error) {
      // Leave the existing markup alone; a failed feed shouldn't blank the page.
      console.warn('[kr-shows]', error);
      return;
    }

    if (!Array.isArray(events)) return;

    this.dataset.krShowsLoaded = 'true';

    if (events.length === 0) {
      list.remove();
      if (empty) empty.hidden = false;
      return;
    }

    // Past shows come back oldest first; a history reads better newest first.
    if (this.isPast) events.reverse();

    const shown = limit > 0 ? events.slice(0, limit) : events;
    list.innerHTML = shown.map((event) => this.#row(event)).join('');
    if (empty) empty.hidden = true;
  }

  #row(event) {
    const date = new Date(event.datetime);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();

    const venue = event.venue?.name || '';
    const city = [event.venue?.city, event.venue?.region || event.venue?.country]
      .filter(Boolean)
      .join(', ');

    // Bandsintown marks sold-out shows in offers; anything else with a ticket
    // offer is on sale, and a show with no offer yet is "on sale soon".
    const offer = (event.offers || []).find((o) => o.type === 'Tickets');
    const soldOut = offer?.status === 'sold out';

    let action = '<span class="tour-dates__status">On Sale Soon</span>';
    if (soldOut) {
      action = '<span class="tour-dates__status">Sold Out</span>';
    } else if (offer?.url) {
      action = `<a class="tour-dates__link" href="${this.#escape(offer.url)}" rel="noopener">Tickets</a>`;
    }

    // A past show carries the year instead of a ticket link: the date is the
    // useful fact, and a dead "Tickets" link on a show from 2023 is worse than none.
    const label = this.isPast ? `${month} ${day}, ${year}` : `${month} ${day}`;
    if (this.isPast) action = '';

    return `
      <li class="tour-dates__row${soldOut && !this.isPast ? ' tour-dates__row--sold-out' : ''}">
        <span class="tour-dates__date">
          <time datetime="${this.#escape(event.datetime)}">${label}</time>
        </span>
        <span class="tour-dates__venue">${this.#escape(venue)}</span>
        <span class="tour-dates__city">${this.#escape(city)}</span>
        <span class="tour-dates__action">${action}</span>
      </li>
    `;
  }

  /** Feed values are third-party strings, so they're escaped before insertion. */
  #escape(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  }
}

if (!customElements.get('kr-shows')) {
  customElements.define('kr-shows', KRShows);
}
